/**
 * Shared dynamic dimensions table helpers (drywall / ceiling metal frames, cross-section accessories).
 */

export const DIMENSIONS_IGNORED_KEYS = new Set([
  "id",
  "displayOrder",
  "createdAtUtc",
  "updatedAtUtc",
  "createdAt",
  "updatedAt",
]);

export const DIMENSIONS_HIDDEN_KEYS = new Set([
  "role",
  "sectionarea",
  "centroid",
  "momentofinertiaixx",
  "momentofinertiaiyy",
  "radiusgyrationrx",
  "radiusgyrationry",
  "yieldstrength",
  "ultimatestrength",
]);

export const DIMENSIONS_PREFERRED_ORDER = [
  "productcode",
  "width",
  "length",
  "thickness",
  "height",
  "h1",
  "h2",
  "unit",
  "yieldstressfy",
  "ultimatestressfu",
];

const STRESS_COLUMN_NORMALIZED = new Set(["yieldstressfy", "ultimatestressfu"]);

export const normalizeKey = (key) =>
  String(key || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

export const toHeaderLabel = (key) => {
  const normalized = normalizeKey(key);
  if (normalized === "productcode") return "Product Code";
  if (normalized === "yieldstressfy") return "Yield stress, Fy";
  if (normalized === "ultimatestressfu") return "Ultimate stress, Fu";
  if (normalized === "role") return "Role";
  if (normalized === "zinccoating") return "Zinc coating";
  if (/^h\d+$/i.test(String(key || ""))) return String(key).toUpperCase();
  if (normalized === "unit") return "Unit";
  return String(key || "")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
};

export const getDimensionValue = (row, key, sectionDefaults = {}) => {
  const entries = Object.entries(row || {});
  const normalizedTarget = normalizeKey(key);
  const direct = entries.find(([k]) => k === key);
  if (direct) return direct[1];
  const caseInsensitive = entries.find(([k]) => normalizeKey(k) === normalizedTarget);
  if (caseInsensitive) return caseInsensitive[1];
  if (normalizedTarget === "productcode") {
    const roleFallback = entries.find(([k]) => normalizeKey(k) === "role");
    if (roleFallback) return roleFallback[1];
  }
  const sectionDefault = Object.entries(sectionDefaults).find(
    ([k]) => normalizeKey(k) === normalizedTarget
  );
  if (sectionDefault) return sectionDefault[1];
  if (normalizedTarget === "unit") return "mm";
  return "";
};

/** Label/value rows from CMS → default values merged into dimension cells (same as metal frames). */
export const sectionDefaultsFromLabelValuePairs = (list) =>
  Array.isArray(list)
    ? list.reduce((acc, x) => {
        const label = String(x?.label || "").trim();
        const value = String(x?.value || "").trim();
        if (!label || !value) return acc;
        acc[label] = value;
        return acc;
      }, {})
    : {};

/** One object per table row; ignores metadata keys; drops empty rows. */
export const toFlexibleDimensionsTableRows = (list) =>
  Array.isArray(list)
    ? list
        .map((x) => (x && typeof x === "object" ? x : {}))
        .filter((row) =>
          Object.entries(row).some(([key, value]) => {
            if (DIMENSIONS_IGNORED_KEYS.has(key)) return false;
            return String(value ?? "").trim() !== "";
          })
        )
    : [];

export const buildDimensionColumnKeys = (dimensionsTable, sectionDefaults) => {
  const rows = Array.isArray(dimensionsTable) ? dimensionsTable : [];
  const sectionDefaultsObj = sectionDefaults && typeof sectionDefaults === "object" ? sectionDefaults : {};
  const seen = new Set();
  const extras = [];
  rows.forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (DIMENSIONS_IGNORED_KEYS.has(key)) return;
      const normalized = normalizeKey(key);
      if (DIMENSIONS_HIDDEN_KEYS.has(normalized)) return;
      if (!normalized || seen.has(normalized)) return;
      seen.add(normalized);
      extras.push({ key, normalized });
    });
  });
  Object.keys(sectionDefaultsObj).forEach((key) => {
    const normalized = normalizeKey(key);
    if (DIMENSIONS_HIDDEN_KEYS.has(normalized)) return;
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    extras.push({ key, normalized });
  });

  const resolved = [];
  DIMENSIONS_PREFERRED_ORDER.forEach((preferred) => {
    const match = extras.find((entry) => entry.normalized === preferred);
    if (match) {
      resolved.push(match.key);
    } else if (preferred === "unit" || preferred === "productcode") {
      resolved.push(preferred);
    }
  });
  extras.forEach((entry) => {
    if (!resolved.some((key) => normalizeKey(key) === entry.normalized)) {
      resolved.push(entry.key);
    }
  });

  const stressLast = (keys) => {
    const stress = [];
    const rest = [];
    keys.forEach((key) => {
      if (STRESS_COLUMN_NORMALIZED.has(normalizeKey(key))) stress.push(key);
      else rest.push(key);
    });
    return [...rest, ...stress];
  };

  return stressLast(resolved);
};
